import React from 'react';
import PropTypes from 'prop-types';
import { InfoAlert } from 'smartsuite-ui';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import TimeSeriesHeatmap from './TimeSeriesHeatmap';
import { formatHeatmapMetricSeries } from '../../utils/heatmapFormatUtils';
import {
    getMetricSourceForRequest,
    hasMetricSources,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import { getTimeSeriesTickFormatter } from '../../utils/dataFormatUtils';

// eslint-disable-next-line complexity
function getMetricSource({ metricRequests, metrics }) {
    if (
        metricRequests.length === 0 ||
        metricRequests[0].groupBy.length === 0 ||
        metricRequests[0].metrics.length === 0
    ) {
        return {
            errorKey: 'invalidMetricRequests',
        };
    }

    const metricSource = getMetricSourceForRequest(metrics, metricRequests[0]);
    if (!metricSource) {
        return {
            errorKey: 'metricSourceNotFound',
        };
    }

    if (!allMetricSourcesAuthorized([metricSource])) {
        return {
            errorKey: 'metricSourceUnauthorized',
        };
    }

    if (!hasMetricSources([metricSource])) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [metricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSource, metricRequest: metricRequests[0] };
}

// eslint-disable-next-line complexity
export default function TimeSeriesHeatmapWrapper(props) {
    const { metricSource, metricRequest, metricSourceErrors, errorKey } =
        getMetricSource(props);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const metric = metricRequest.metrics[0];
    const groupBy = metricRequest.groupBy[0];
    const formattedData = formatHeatmapMetricSeries(
        metricSource.bucketTimes,
        metricSource.metricSeries,
        metric,
        groupBy
    );
    const timeAxisFormatter = getTimeSeriesTickFormatter(
        metricRequest.timeAndPrecision.precisionType
    );

    return (
        <>
            {metricSource.isTruncated && (
                <InfoAlert>
                    {`${props.dashboardLabels.truncationWarning} ${metricRequest.truncationThreshold}`}
                </InfoAlert>
            )}
            <div className="chart-container">
                <TimeSeriesHeatmap
                    {...props}
                    bucketTimes={metricSource.bucketTimes}
                    formattedData={formattedData}
                    timeAxisFormatter={timeAxisFormatter}
                />
            </div>
        </>
    );
}

TimeSeriesHeatmapWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
};
